import { Link } from "react-router-dom";
import { motion } from "@/lib/motion";
import { Sparkles, Zap, Shield, TrendingUp, ArrowLeft, Clock } from "lucide-react";

export const PremiumCtaBanner = () => {
  return (
    <section className="relative py-9 md:py-12 overflow-hidden border-t border-foreground/10 bg-surface/50">
      {/* Background Effects */}
      <div className="absolute inset-0 pointer-events-none">
        <motion.div
          className="absolute top-1/2 left-1/4 w-96 h-96 rounded-full"
          style={{
            background: 'radial-gradient(circle, hsl(var(--sand)) 0%, transparent 70%)',
            opacity: 0.1,
          }}
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.1, 0.2, 0.1],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <motion.div
          className="absolute top-1/2 right-1/4 w-80 h-80 rounded-full"
          style={{
            background: 'radial-gradient(circle, hsl(var(--primary)) 0%, transparent 70%)',
            opacity: 0.08,
          }}
          animate={{
            scale: [1, 1.4, 1],
            opacity: [0.08, 0.15, 0.08],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1,
          }}
        />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl mx-auto"
        >
          {/* Premium CTA Box */}
          <motion.div 
            className="relative overflow-hidden rounded-3xl p-[2px] group"
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.3 }}
          >
            {/* Animated Gradient Border */}
            <motion.div
              className="absolute inset-0 rounded-3xl"
              style={{
                background: 'linear-gradient(135deg, hsl(var(--sand)), hsl(var(--primary)), hsl(var(--accent)), hsl(var(--sand)))',
                backgroundSize: '300% 300%',
              }}
              animate={{
                backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
              }}
              transition={{
                duration: 5,
                repeat: Infinity,
                ease: "linear",
              }}
            />

            {/* Inner Content */}
            <div className="relative bg-background rounded-3xl p-6 md:p-7 text-center">
              {/* Glow Effect */}
              <motion.div
                className="absolute -top-20 -right-20 w-40 h-40 rounded-full opacity-20"
                style={{
                  background: 'radial-gradient(circle, hsl(var(--sand)) 0%, transparent 70%)',
                }}
                animate={{
                  scale: [1, 1.5, 1],
                  opacity: [0.2, 0.4, 0.2],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />

              {/* Top Badge */}
              <motion.div 
                className="inline-flex items-center gap-1.5 bg-sand/10 px-3 py-1.5 rounded-full mb-3"
                animate={{
                  scale: [1, 1.05, 1],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                }}
              >
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                >
                  <Sparkles className="w-4 h-4 text-sand" />
                </motion.div>
                <span className="text-xs font-bold text-sand">عرض حصري</span>
              </motion.div>

              {/* Main Headline */}
              <h3 className="font-display font-bold text-sand mb-2 text-xl md:text-2xl leading-tight">
                جاهز للنمو؟
              </h3>

              {/* Sub Headline */}
              <p className="text-sm md:text-base text-foreground/80 mb-1.5 font-medium">
                استشارة مجانية + تحليل منافسين
              </p>
              <p className="text-xs text-foreground/60 mb-4">
                اكتشف كيف نضاعف نتائجك الرقمية
              </p>

              {/* Trust Badges */}
              <div className="flex items-center justify-center gap-2 mb-4 flex-wrap">
                <div className="flex items-center gap-1.5 bg-foreground/5 px-3 py-1.5 rounded-lg">
                  <Zap className="w-4 h-4 text-sand" />
                  <span className="text-xs text-foreground/70">رد فوري</span>
                </div>
                <div className="flex items-center gap-1.5 bg-foreground/5 px-3 py-1.5 rounded-lg">
                  <Shield className="w-4 h-4 text-sand" />
                  <span className="text-xs text-foreground/70">ضمان النتائج</span>
                </div>
                <div className="flex items-center gap-1.5 bg-foreground/5 px-3 py-1.5 rounded-lg">
                  <TrendingUp className="w-4 h-4 text-sand" />
                  <span className="text-xs text-foreground/70">+287% نمو</span>
                </div>
              </div>

              {/* CTA Button */}
              <Link to="/contact" className="block">
                <motion.button
                  className="relative w-full max-w-md mx-auto bg-sand text-background px-6 py-3 rounded-xl text-sm font-bold overflow-hidden group cursor-pointer shadow-lg shadow-sand/30 hover:shadow-xl hover:shadow-sand/40 transition-all"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {/* Animated Border Pulse */}
                  <motion.div
                    className="absolute inset-0 border-2 border-white/40 rounded-xl"
                    animate={{
                      scale: [1, 1.05, 1],
                      opacity: [0.5, 0, 0.5],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  />

                  {/* Button Glow */}
                  <motion.div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{
                      background: 'radial-gradient(circle at center, rgba(255,255,255,0.3) 0%, transparent 70%)',
                    }}
                  />
                  
                  {/* Button Content */}
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    <motion.span
                      className="inline-block text-base"
                      animate={{ rotate: [0, 15, 0] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    >
                      👆
                    </motion.span>
                    احجز استشارتك المجانية
                    <motion.div
                      animate={{ x: [0, 5, 0] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    >
                      <ArrowLeft className="w-4 h-4" />
                    </motion.div>
                  </span>

                  {/* Shine Effect */}
                  <motion.div
                    className="absolute inset-0 w-1/2 h-full bg-gradient-to-r from-transparent via-white/40 to-transparent"
                    animate={{
                      x: ['-100%', '200%'],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      repeatDelay: 0.5,
                      ease: "easeInOut",
                    }}
                  />
                </motion.button>
              </Link>

              {/* Bottom Note */}
              <Link to="/contact" className="block mt-2 group/note cursor-pointer">
                <motion.p 
                  className="text-xs text-foreground/50 flex items-center justify-center gap-1 hover:text-sand transition-colors relative"
                  whileHover={{ scale: 1.05 }}
                >
                  <motion.span
                    animate={{ 
                      scale: [1, 1.2, 1],
                    }}
                    transition={{ duration: 1, repeat: Infinity }}
                  >
                    <Clock className="w-3 h-3" />
                  </motion.span>
                  <span className="relative">
                    يستغرق دقيقتين فقط
                    <motion.span
                      className="absolute bottom-0 left-0 right-0 h-[1px] bg-sand origin-right"
                      initial={{ scaleX: 0 }}
                      whileHover={{ scaleX: 1 }}
                      transition={{ duration: 0.3 }}
                    />
                  </span>
                  <motion.span
                    className="inline-block"
                    animate={{ x: [0, 3, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    ←
                  </motion.span>
                </motion.p>
              </Link>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
